import { useTranslation } from 'react-i18next';
import type { CreatorWork } from '@/api/member';
import EarningWithdrawal from '../../components/EarningWithdrawal';

const fmtView = (n: number) => (n >= 10000 ? `${(n / 10000).toFixed(1)}万` : n.toLocaleString());
const fmtMoney = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

export default function OverviewPanel({ works }: { works: CreatorWork[] }) {
  const { t } = useTranslation('cinema');

  const published = works.filter((w) => w.status === 'published');
  const totalViews = published.reduce((s, w) => s + w.views, 0);
  const totalSubs = published.reduce((s, w) => s + w.subscriptions, 0);
  const totalRevenue = published.reduce((s, w) => s + w.revenue, 0);
  const reviewCount = works.filter((w) => !['published', 'rejected'].includes(w.status)).length;
  const rejectedCount = works.filter((w) => w.status === 'rejected').length;
  const topWork = published.slice().sort((a, b) => b.views - a.views)[0] ?? null;

  const stats = [
    {
      label: t('cinema_creatorStatViews'),
      value: fmtView(totalViews),
      icon: 'ri-eye-line',
      tone: 'text-accent-300 bg-accent-500/15',
    },
    {
      label: t('cinema_creatorStatSubs'),
      value: totalSubs.toLocaleString(),
      icon: 'ri-user-heart-line',
      tone: 'text-amber-300 bg-amber-500/15',
    },
    {
      label: t('cinema_creatorStatRevenue'),
      value: `¥${fmtMoney(totalRevenue)}`,
      icon: 'ri-money-cny-circle-line',
      tone: 'text-emerald-300 bg-emerald-500/15',
    },
  ];

  return (
    <div className="space-y-4">
      <EarningWithdrawal source="creator" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-2xl bg-background-900/50 border border-background-800/40 p-4 md:p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-foreground-500">{s.label}</span>
              <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.tone}`}>
                <i className={`${s.icon} text-base`}></i>
              </span>
            </div>
            <p className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-background-900/40 border border-background-800/40 p-3.5 flex items-center gap-3">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-foreground-50/5">
            <i className="ri-film-line text-accent-400"></i>
          </span>
          <div>
            <p className="text-lg font-bold text-foreground-50">{works.length}</p>
            <p className="text-[11px] text-foreground-500">{t('cinema_creatorStatWorks')}</p>
          </div>
        </div>
        <div className="rounded-xl bg-background-900/40 border border-background-800/40 p-3.5 flex items-center gap-3">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-500/15">
            <i className="ri-time-line text-amber-400"></i>
          </span>
          <div>
            <p className="text-lg font-bold text-foreground-50">{reviewCount}</p>
            <p className="text-[11px] text-foreground-500">{t('cinema_creatorStatReview')}</p>
          </div>
        </div>
        <div className="rounded-xl bg-background-900/40 border border-background-800/40 p-3.5 flex items-center gap-3">
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-rose-500/15">
            <i className="ri-close-circle-line text-rose-400"></i>
          </span>
          <div>
            <p className="text-lg font-bold text-foreground-50">{rejectedCount}</p>
            <p className="text-[11px] text-foreground-500">{t('cinema_creatorStatRejected')}</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-background-900/50 border border-background-800/40 p-4 md:p-5">
        <h4 className="text-sm font-bold text-foreground-100 mb-4 flex items-center gap-2">
          <i className="ri-trophy-line text-amber-400"></i>
          {t('cinema_creatorTopWork')}
        </h4>
        {topWork ? (
          <div className="flex items-center gap-4">
            <img
              src={topWork.coverUrl}
              alt={topWork.title}
              className="w-16 md:w-20 rounded-lg object-cover flex-shrink-0"
              style={{ aspectRatio: '2/3', height: 'auto' }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h5 className="text-sm md:text-base font-semibold text-foreground-100">{topWork.title}</h5>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-background-950 bg-accent-500 whitespace-nowrap">
                  {t('cinema_creatorOriginal')}
                </span>
              </div>
              <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2">{topWork.description}</p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
                <div className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-accent-400"></i>
                  <span className="text-sm font-bold text-foreground-100">{fmtView(topWork.views)}</span>
                  <span className="text-[11px] text-foreground-500">{t('cinema_creatorViews')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-user-heart-line text-amber-400"></i>
                  <span className="text-sm font-bold text-foreground-100">{topWork.subscriptions.toLocaleString()}</span>
                  <span className="text-[11px] text-foreground-500">{t('cinema_creatorSubscriptions')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-money-cny-circle-line text-emerald-400"></i>
                  <span className="text-sm font-bold text-foreground-100">¥{fmtMoney(topWork.revenue)}</span>
                  <span className="text-[11px] text-foreground-500">{t('cinema_creatorShare')}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground-500">{t('cinema_creatorNoPublished')}</p>
        )}
      </div>
    </div>
  );
}
